import React, { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDownIcon } from "../icons";

const CustomMultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select...",
  maxVisibleChips = 2,
  displayFormatter = (val) => val, // Optional formatter for display text
  searchable = false,
  searchPlaceholder = "Type to filter…",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      return undefined;
    }
    if (!searchable) return undefined;
    const t = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [isOpen, searchable]);

  const selectedValues = Array.isArray(value) ? value : [];

  const filteredOptions = useMemo(() => {
    if (!searchable) return options;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return options;
    return options.filter((option) => {
      const label = String(displayFormatter(option) ?? option).toLowerCase();
      const raw = String(option ?? "").toLowerCase();
      return label.includes(q) || raw.includes(q);
    });
  }, [options, searchable, searchQuery, displayFormatter]);

  const handleToggle = (option) => {
    const newValue = selectedValues.includes(option)
      ? selectedValues.filter((v) => v !== option)
      : [...selectedValues, option];
    onChange(newValue);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const handleRemoveChip = (e, option) => {
    e.stopPropagation();
    const newValue = selectedValues.filter((v) => v !== option);
    onChange(newValue);
  };

  const visibleChips = selectedValues.slice(0, maxVisibleChips);
  const remainingCount = selectedValues.length - maxVisibleChips;

  return (
    <div className="relative w-full" ref={containerRef}>
      {/* Input field with chips */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        data-open={isOpen ? "true" : "false"}
        className="ims-dropdown-trigger w-full pr-10 pl-3 py-1.5 border border-[var(--line-strong,#d5d3cc)] rounded-md text-[13px] cursor-pointer flex items-center gap-1 min-h-[38px] outline-none"
      >
        <div className="flex-1 flex items-center gap-1 flex-wrap min-h-[20px]">
          {selectedValues.length === 0 ? (
            <span className="text-gray-400 text-sm">{placeholder}</span>
          ) : (
            <>
              {visibleChips.map((option) => (
                <span
                  key={option}
                  className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-1.5 py-0.5 rounded font-medium"
                >
                  <span>{displayFormatter(option)}</span>
                  <button
                    onClick={(e) => handleRemoveChip(e, option)}
                    className="hover:bg-gray-200 rounded-full p-0.5 transition-colors"
                    type="button"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </span>
              ))}
              {remainingCount > 0 && (
                <span className="text-xs text-gray-600 font-medium">
                  +{remainingCount} more
                </span>
              )}
            </>
          )}
        </div>
        <div className="absolute right-2 flex items-center gap-1">
          {selectedValues.length > 0 && (
            <button
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 p-0.5 rounded transition-colors"
              type="button"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
          <ChevronDownIcon
            className={`w-4 h-4 text-gray-500 transition-transform ${
              isOpen ? "transform rotate-180" : ""
            }`}
          />
        </div>
      </div>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="ims-dropdown-panel absolute z-50 w-full mt-1 bg-white border border-[var(--line,#e4e2dc)] rounded-md shadow-lg max-h-60 overflow-hidden flex flex-col">
          {searchable ? (
            <div className="sticky top-0 z-10 border-b border-[var(--line,#e4e2dc)] bg-white p-2">
              <input
                ref={searchInputRef}
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder={searchPlaceholder}
                className="w-full rounded-md border border-[var(--line-strong,#d5d3cc)] px-2.5 py-1.5 text-[13px] text-[var(--ink,#1c1c1a)] outline-none focus:ring-1 focus:ring-[var(--accent,#a51c1c)]"
                aria-label={searchPlaceholder}
              />
            </div>
          ) : null}
          <div className="py-1 overflow-y-auto overflow-x-hidden flex-1 min-h-0">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                {options.length === 0
                  ? "No options available"
                  : "No matching options"}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option);
                return (
                  <label
                    key={option}
                    className="ims-dropdown-option flex items-start px-3 py-2 text-sm cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggle(option)}
                      className="mt-0.5 w-4 h-4 shrink-0 border-[var(--line-strong,#d5d3cc)] rounded"
                    />
                    <span className="ml-2 min-w-0 flex-1 break-words whitespace-normal text-gray-700">
                      {displayFormatter(option)}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomMultiSelect;

